<script>
  // SFTP bucket editor as a PAGE (/sftp/new to create, /sftp/{slug} to edit),
  // not a dialog. It owns everything a bucket carries: name, retention,
  // enabled, the allowed-source-IP list, and N credentials with N authorized
  // keys each. It used to be a modal, which is a bad fit for a list that grows
  // -- ten keys and six addresses do not fit a phone-sized dialog, and that is
  // exactly where this gets edited.
  //
  // Shell mirrors BucketDetail: back affordance, load-by-id with a $effect on
  // the prop, its own not-found state, and saving through the same api.* calls
  // the modal used.
  import { navigate } from 'svelte-routing';
  import { api } from '../lib/api.js';
  import { ui } from '../lib/ui.svelte.js';
  import { getUser } from '../lib/auth.js';
  import TextField from '../components/TextField.svelte';
  import SftpKeygenDialog from '../components/SftpKeygenDialog.svelte';

  let { id } = $props();

  const me = getUser();
  const isAdmin = me?.portal_role === 'admin';
  const isNew = $derived(id === 'new');

  let meta = $state({ retention_default: 7, retention_min: 1, retention_max: 30 });
  let bucket = $state(null); // the saved doc; stays null on /sftp/new
  let loading = $state(true);
  let err = $state('');
  let saving = $state(false);

  // ---- Draft ---------------------------------------------------------
  // Same shapes the modal's openEdit built, including its tolerance for the
  // pre-label allowed-IP form (a bare string).
  let name = $state('');
  let slug = $state('');
  let retention = $state('7');
  let enabled = $state(true);
  // Allowed source IPs: [{ ip, label }]. Starts EMPTY on a new bucket because
  // an empty list means "any IP may log in"; a blank starter row would imply an
  // entry is expected.
  let allowedIps = $state([]);
  // Credentials: [{ username, password, keys: [{ line, label }] }].
  let creds = $state([]);
  const blankCred = () => ({ username: '', password: '', keys: [] });

  async function load() {
    loading = true; err = '';
    // Meta only feeds hints and the create-time retention default, so a failed
    // read falls back to the constants above rather than blocking the editor.
    //
    // The await comes FIRST on purpose. Written as
    //   meta = { ...meta, ...(await api.sftpMeta()) }
    // the `...meta` read is evaluated before the await — while load()'s caller
    // ($effect) is still the active reaction — so `meta` becomes a dependency of
    // that effect, and the very same statement reassigns it. The effect
    // invalidates itself and load() re-runs forever: measured at ~33 reloads a
    // second, which also silently discarded whatever the operator had typed and
    // reset the dirty baseline, so Save then sent allowed_ips: [] and cleared the
    // restriction. Read `meta` only after the await.
    try {
      const m = await api.sftpMeta();
      meta = { ...meta, ...m };
    } catch (_) {}
    try {
      if (isNew) {
        bucket = null;
        name = ''; slug = ''; retention = String(meta.retention_default || 7); enabled = true;
        allowedIps = [];
        creds = [blankCred()];
      } else {
        const b = await api.getSftpBucket(id);
        bucket = b;
        name = b.name || '';
        slug = b.id;
        retention = String(b.retention_days || 7);
        enabled = b.enabled !== false;
        // Tolerate the pre-label shape (a bare string) as well as { ip, label }:
        // the server accepts both during a rollout, and this side has to survive
        // reading a response from a replica that has not been updated yet.
        allowedIps = (b.allowed_ips || []).map((e) =>
          typeof e === 'string' ? { ip: e, label: '' } : { ip: e.ip || '', label: e.label || '' },
        );
        creds = (b.credentials || []).map((c) => ({
          username: c.username,
          password: c.password || '',
          keys: (c.authorized_keys || []).map((k) => ({ line: k.public_key, label: k.label || '' })),
        }));
        if (creds.length === 0) creds = [blankCred()];
      }
      baseline = snapshot();
    } catch (e) { err = e.message; }
    finally { loading = false; }
  }

  // Re-load whenever the route's :id changes (the component instance can stay
  // mounted across /sftp/new -> /sftp/{slug}).
  $effect(() => { id; load(); });

  function addCred() { creds = [...creds, blankCred()]; }
  function removeCred(i) { creds = creds.filter((_, idx) => idx !== i); }
  function addKey(ci) { creds[ci].keys = [...creds[ci].keys, { line: '', label: '' }]; }
  function removeKey(ci, ki) { creds[ci].keys = creds[ci].keys.filter((_, idx) => idx !== ki); }
  function addAllowedIp() { allowedIps = [...allowedIps, { ip: '', label: '' }]; }
  function removeAllowedIp(i) { allowedIps = allowedIps.filter((_, idx) => idx !== i); }

  // True when a credential has at least one non-empty key field.
  const hasKeys = (c) => c.keys.some((k) => k.line.trim());
  // Blank rows don't count -- an all-blank list is still "any IP", and the hint
  // under the field has to say so while the admin is still typing.
  const allowedIpCount = $derived(allowedIps.filter((e) => e.ip.trim()).length);

  // ---- Unsaved-changes tracking --------------------------------------
  // A canonical snapshot of the draft, compared against the one taken when the
  // form was populated. Trimmed the same way buildCredsPayload trims, so
  // whitespace the server would drop anyway never counts as a change.
  function snapshot() {
    return JSON.stringify({
      name: name.trim(),
      slug: slug.trim(),
      retention: String(retention).trim(),
      enabled,
      ips: allowedIps.map((e) => [e.ip.trim(), (e.label || '').trim()]),
      creds: creds.map((c) => [
        c.username.trim(),
        c.password,
        c.keys.map((k) => [k.line.trim(), (k.label || '').trim()]),
      ]),
    });
  }
  let baseline = $state('');
  let leaving = $state(false); // set once a departure is agreed, so the guard stops asking
  const dirty = $derived(!loading && !err && baseline !== '' && snapshot() !== baseline);

  function confirmDiscard() {
    return ui.ask({
      title: 'Discard unsaved changes?',
      message: 'This bucket has edits that have not been saved. Leaving loses them.',
      confirmText: 'Discard',
      cancelText: 'Keep editing',
      destructive: true,
    });
  }

  // Two departure routes get guarded, and they are the two that exist:
  //
  //  - a hard navigation (reload, tab close, a link off the SPA) -> beforeunload;
  //  - an in-app link click (the nav rail) -> intercepted in the CAPTURE phase,
  //    which runs before svelte-routing's own handler on the <a>, so the route
  //    has not changed yet and this component is still alive to ask. Only
  //    preventDefault, never stopPropagation: svelte-routing's shouldNavigate
  //    already bails on a defaultPrevented event, so cancelling is enough to
  //    hold the route while the shell's own click handlers still run and close
  //    the mobile drawer and the profile menu.
  //
  // The browser BACK button is deliberately NOT guarded: popstate fires after
  // the router has already swapped the route out, and the only way to prompt
  // there is to push a sentinel history entry -- which is exactly the kind of
  // "back does nothing on the first press" trap worth avoiding. Back means back.
  $effect(() => {
    function onBeforeUnload(e) {
      if (!dirty || leaving) return;
      e.preventDefault();
      e.returnValue = ''; // required by older browsers to show the prompt
    }
    async function onCaptureClick(e) {
      if (!dirty || leaving || e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      // A link that opens elsewhere or downloads never unmounts this page.
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      // Off-origin leaves the SPA entirely -- beforeunload above covers that.
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      if (await confirmDiscard()) {
        leaving = true;
        navigate(url.pathname + url.search);
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('click', onCaptureClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('click', onCaptureClick, true);
    };
  });

  // ---- Leaving -------------------------------------------------------
  // Pushed from the list in this tab -> a real back() keeps the stack honest
  // (no duplicate /sftp entry to press through afterwards). A deep link or a
  // fresh tab has no such entry, so replace this one instead of pushing, or
  // back would bounce onto a page the user never visited.
  const cameFromList = typeof history !== 'undefined' && history.state?.sftpFromList === true;
  function goList() {
    if (cameFromList) history.back();
    else navigate('/sftp', { replace: true });
  }
  async function cancel() {
    if (dirty && !(await confirmDiscard())) return;
    leaving = true;
    goList();
  }

  // ---- Save ----------------------------------------------------------
  function buildCredsPayload() {
    return creds
      .filter((c) => c.username.trim())
      .map((c) => ({
        username: c.username.trim(),
        password: c.password,
        authorized_keys: c.keys
          .map((k) => ({ line: k.line.trim(), label: (k.label || '').trim() }))
          .filter((k) => k.line)
          .map((k) => ({ public_key: k.line, label: k.label })),
      }));
  }

  async function save() {
    const payloadCreds = buildCredsPayload();
    if (isNew && payloadCreds.length === 0) {
      ui.snack('Add at least one credential (username)', 'error');
      return;
    }
    const body = {
      name: name.trim(),
      retention_days: Number(retention) || meta.retention_default || 7,
      credentials: payloadCreds,
      // Always sent, so removing the last row clears the restriction (= any IP).
      // A row with a label but no address is a blank row, so the address is what
      // decides whether the entry is sent at all.
      allowed_ips: allowedIps
        .map((e) => ({ ip: e.ip.trim(), label: (e.label || '').trim() }))
        .filter((e) => e.ip),
    };
    saving = true;
    try {
      if (isNew) {
        // No `enabled` on create: the server always creates a bucket enabled
        // and its create body has no such field, which is why this page only
        // offers the toggle when editing.
        await api.createSftpBucket({ ...body, slug: slug.trim() });
        ui.snack('Bucket created', 'success');
      } else {
        await api.updateSftpBucket(id, { ...body, enabled });
        ui.snack('Bucket updated', 'success');
      }
      baseline = snapshot(); // clean again, so leaving does not prompt
      leaving = true;
      goList();
    } catch (e) {
      ui.snack(e.message, 'error');
    } finally {
      saving = false;
    }
  }

  // ---- Generate keypair (draft credential) ---------------------------
  let genFor = $state(null); // credential index, or null when closed
  // The public key is appended to the draft credential and persists on save;
  // the dialog shows the private key once. The label is seeded from the comment
  // the operator typed, so a generated key arrives identifiable rather than as
  // an unlabelled blob.
  function generateForCred(ci) {
    return async (type, bits, comment) => {
      const r = await api.generateSftpKeypair(type, bits, comment || 'hook-index');
      creds[ci].keys = [...creds[ci].keys, { line: r.public_key, label: comment }];
      return r;
    };
  }
</script>

<div class="sbd">
  {#if !isAdmin}
    <div class="sbd-head">
      <button type="button" class="sbd-back" onclick={goList} aria-label="Back to SFTP buckets">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h2 class="sbd-title">SFTP bucket</h2>
    </div>
    <p class="text-on-surface-variant">Only admins can create or edit SFTP buckets.</p>
  {:else if loading}
    <p class="text-on-surface-variant">Loading…</p>
  {:else if err}
    <div class="sbd-head">
      <button type="button" class="sbd-back" onclick={goList} aria-label="Back to SFTP buckets">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h2 class="sbd-title">SFTP bucket</h2>
    </div>
    <p class="text-error">{err}</p>
    <button type="button" class="sbd-btn" onclick={goList}>Back to buckets</button>
  {:else}
    <div class="sbd-head">
      <button type="button" class="sbd-back" onclick={cancel} aria-label="Back to SFTP buckets">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <div class="sbd-headtext">
        <h2 class="sbd-title">{isNew ? 'New SFTP bucket' : bucket?.name || 'SFTP bucket'}</h2>
        {#if !isNew}<div class="sbd-slug">slug: <span class="font-mono">{slug}</span></div>{/if}
      </div>
      {#if dirty}<span class="sbd-dirty">Unsaved</span>{/if}
    </div>

    <form class="sbd-form" onsubmit={(e) => { e.preventDefault(); save(); }}>
      <section class="sbd-card">
        <h3 class="sbd-section">Bucket</h3>
        <div class="sbd-fields">
          <TextField label="Name" bind:value={name} />
          {#if isNew}
            <TextField label="Slug (optional)" bind:value={slug} autocapitalize="none" spellcheck="false" />
          {/if}
          <TextField
            label={`Retention days (${meta.retention_min}–${meta.retention_max})`}
            bind:value={retention}
            inputmode="numeric"
          />
          {#if !isNew}
            <label class="sbd-toggle">
              <input type="checkbox" bind:checked={enabled} class="sbd-checkbox" />
              <span>Enabled</span>
            </label>
          {/if}
        </div>
        {#if isNew}
          <p class="sbd-hint">
            <span class="material-symbols-outlined">info</span>
            Leave the slug blank to derive one from the name. New buckets start enabled.
          </p>
        {/if}
      </section>

      <section class="sbd-card">
        <h3 class="sbd-section">Allowed source IPs</h3>
        <div class="sbd-ips">
          <p class="sbd-sublabel">One row per address — bare IP or CIDR, with an optional name. Leave the list empty to accept any IP.</p>
          {#if allowedIps.length}
            <div class="sbd-ip-rows">
              {#each allowedIps as _entry, i}
                <div class="sbd-ip-row">
                  <div class="sbd-tag">
                    <TextField label="Name (optional)" bind:value={allowedIps[i].label} spellcheck="false" />
                  </div>
                  <div class="sbd-input">
                    <TextField
                      label="IP address or CIDR"
                      bind:value={allowedIps[i].ip}
                      spellcheck="false"
                      autocapitalize="none"
                    />
                  </div>
                  <button type="button" class="sbd-remove" title="Remove IP" aria-label="Remove IP" onclick={() => removeAllowedIp(i)}>
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </div>
              {/each}
            </div>
          {/if}
          <div class="sbd-rowactions">
            <button type="button" class="sbd-chip" onclick={addAllowedIp}>
              <span class="material-symbols-outlined text-base">add</span> Add IP
            </button>
          </div>
        </div>
        <p class="sbd-hint">
          {#if allowedIpCount === 0}
            <span class="material-symbols-outlined">public</span>
            Empty list — <strong>any IP address may log in</strong> to this bucket with a valid username.
          {:else}
            <span class="material-symbols-outlined">shield</span>
            <strong>Only</strong> these {allowedIpCount} source{allowedIpCount === 1 ? '' : 's'} may log in — every
            other address is refused, even with the right password or key.
          {/if}
        </p>
      </section>

      <section class="sbd-card">
        <h3 class="sbd-section">Credentials</h3>
        <div class="sbd-creds">
          {#each creds as c, i}
            <div class="sbd-cred">
              <div class="sbd-cred-user">
                <TextField label="Username" bind:value={c.username} autocapitalize="none" autocomplete="off" spellcheck="false" />
              </div>
              <div class="sbd-cred-pass">
                <TextField label="Password (optional)" type="password" bind:value={c.password} autocomplete="new-password" />
              </div>
              <button type="button" class="sbd-remove sbd-cred-remove" title="Remove credential" aria-label="Remove credential" onclick={() => removeCred(i)}>
                <span class="material-symbols-outlined">close</span>
              </button>

              <div class="sbd-keys">
                <p class="sbd-sublabel">Authorized public keys — one field per key (any type)</p>
                {#if c.keys.length}
                  <div class="sbd-key-rows">
                    {#each c.keys as _key, ki}
                      <div class="sbd-key-row">
                        <div class="sbd-tag">
                          <TextField label="Key name (optional)" bind:value={c.keys[ki].label} spellcheck="false" />
                        </div>
                        <div class="sbd-input">
                          <TextField
                            label="Public key"
                            bind:value={c.keys[ki].line}
                            spellcheck="false"
                            autocapitalize="none"
                          />
                        </div>
                        <button type="button" class="sbd-remove" title="Remove key" aria-label="Remove key" onclick={() => removeKey(i, ki)}>
                          <span class="material-symbols-outlined">close</span>
                        </button>
                      </div>
                    {/each}
                  </div>
                {/if}
                <div class="sbd-rowactions">
                  <button type="button" class="sbd-chip" onclick={() => addKey(i)}>
                    <span class="material-symbols-outlined text-base">add</span> Add key
                  </button>
                  <button type="button" class="sbd-chip" onclick={() => (genFor = i)}>
                    <span class="material-symbols-outlined text-base">key</span> Generate keypair
                  </button>
                </div>
              </div>

              <p class="sbd-hint">
                {#if !c.password.trim() && !hasKeys(c)}
                  <span class="material-symbols-outlined sbd-hint-warn">info</span>
                  No auth set — add a password and/or a key.
                {:else if !c.password.trim()}
                  <span class="material-symbols-outlined">key</span> Key-only login — no password auth.
                {:else if !hasKeys(c)}
                  <span class="material-symbols-outlined">password</span> Password-only login.
                {:else}
                  <span class="material-symbols-outlined">verified_user</span> Password and key both accepted.
                {/if}
              </p>
            </div>
          {/each}
        </div>
        <div class="sbd-rowactions">
          <button type="button" class="sbd-chip" onclick={addCred}>
            <span class="material-symbols-outlined text-base">add</span> Add credential
          </button>
        </div>
      </section>

      <!-- Sticky so Save is one thumb-reach away however many keys and
           addresses are on the page. -->
      <div class="sbd-actions">
        <button type="button" class="sbd-btn" onclick={cancel} disabled={saving}>Cancel</button>
        <button type="submit" class="sbd-btn sbd-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create bucket' : 'Save changes'}
        </button>
      </div>
    </form>
  {/if}
</div>

{#if genFor != null && isAdmin}
  <SftpKeygenDialog
    subject={creds[genFor]?.username || 'this credential'}
    defaultComment={(creds[genFor]?.username || '').trim()}
    generate={generateForCred(genFor)}
    onClose={() => (genFor = null)}
  />
{/if}

<style>
  /* Sizing is asked of the PAGE'S OWN BOX, never the viewport. This editor
     renders inside the shell's content panel, which is narrower than the window
     by the nav rail plus its gutters -- and a sibling app shipped a bug where a
     `max-width: 600px` media query never matched for a 379px-wide panel inside
     a 1360px window, so every "full width" field rendered at 155px. @container
     asks the box, so the answer stays right wherever the page is placed. */
  .sbd {
    min-block-size: 100%;
    /* The shell's content panel is the only scroller; nothing here adds a
       second one, so the sticky action bar below tracks the page scroll. */
  }

  .sbd-head { display: flex; align-items: center; gap: 0.5rem; margin-block-end: 1rem; }
  .sbd-back {
    flex: none;
    inline-size: 44px; block-size: 44px;
    display: inline-flex; align-items: center; justify-content: center;
    border: 0; border-radius: 50%;
    background: transparent; color: rgb(var(--md-sys-color-on-surface));
    cursor: pointer;
  }
  .sbd-back:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .sbd-headtext { min-inline-size: 0; }
  .sbd-title { font-size: 1.5rem; font-weight: 500; margin: 0; overflow-wrap: anywhere; }
  .sbd-slug { font-size: 12px; color: rgb(var(--md-sys-color-on-surface-variant)); }
  .sbd-dirty {
    margin-inline-start: auto; flex: none;
    font-size: 11px; font-weight: 600;
    padding: 2px 8px; border-radius: 999px;
    background: rgb(var(--md-sys-color-tertiary-container, var(--md-sys-color-primary-container)));
    color: rgb(var(--md-sys-color-on-tertiary-container, var(--md-sys-color-on-primary-container)));
  }

  /* Block layout, deliberately not a grid: a grid item's containing block is its
     grid area, and the action bar sits in a row exactly its own height -- so
     `position: sticky` on it would have nowhere to travel and would silently
     stop sticking. Margins give the same spacing with none of that. */
  .sbd-form { display: block; }
  .sbd-card {
    margin-block-end: 0.75rem;
    /* Longhands, not the `container` shorthand: that one reads
       `<name> / <type>`, which is easy to write backwards, and a silently
       inert container-type would take the whole layout below down with it. */
    container-type: inline-size;
    container-name: sbd-card;
    background: rgb(var(--md-sys-color-surface));
    --md-tf-surface: var(--md-sys-color-surface);
    border-radius: 16px;
    padding: 1rem;
  }
  .sbd-section {
    font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin: 0 0 0.75rem;
  }
  .sbd-sublabel { font-size: 12px; color: rgb(var(--md-sys-color-on-surface-variant)); margin: 0 0 0.5rem; }
  .sbd-hint {
    display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
    font-size: 12px; color: rgb(var(--md-sys-color-on-surface-variant));
    margin: 0.5rem 0 0;
  }
  .sbd-hint .material-symbols-outlined { font-size: 16px; }
  .sbd-hint-warn { color: rgb(var(--md-sys-color-error)); }

  /* One field per line until the CARD has room for more. */
  .sbd-fields { display: grid; grid-template-columns: minmax(0, 1fr); gap: 0.75rem; align-items: center; }
  @container sbd-card (inline-size >= 30rem) {
    .sbd-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @container sbd-card (inline-size >= 46rem) {
    .sbd-fields { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  }

  .sbd-toggle { display: inline-flex; align-items: center; gap: 0.5rem; min-block-size: 44px; font-size: 0.9rem; cursor: pointer; }
  .sbd-checkbox { inline-size: 20px; block-size: 20px; flex: none; accent-color: rgb(var(--md-sys-color-primary)); cursor: pointer; }

  /* Repeated rows are grid + subgrid, never flex-per-row. Flex sized every row
     independently, which wrapped "ssh-ed25519" onto a second line on a phone
     and left the copy icons ragged down the list (fixed once in b36ffda for the
     card's key list -- same fix, same reason, applied to the editors). Tracks
     live on the list; each row inherits them, so the name box, the value box and
     the remove button line up however many rows there are. */
  .sbd-ips { container-type: inline-size; container-name: sbd-ips; }
  .sbd-ip-rows { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0.5rem; margin-block-end: 0.5rem; }
  .sbd-ip-row { display: grid; grid-template-columns: subgrid; grid-column: 1 / -1; gap: 0.5rem; align-items: center; }
  /* Narrow: the name takes a line of its own above the address, which is too
     narrow to share. */
  .sbd-ip-row > .sbd-tag { grid-column: 1 / -1; }
  @container sbd-ips (inline-size >= 34rem) {
    .sbd-ip-rows { grid-template-columns: clamp(9rem, 24%, 13rem) minmax(0, 1fr) auto; }
    .sbd-ip-row > .sbd-tag { grid-column: auto; }
  }

  /* Credential cards subgrid the list's columns, so username / password /
     remove align across every credential instead of each card sizing itself. */
  .sbd-creds { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0.75rem; }
  .sbd-cred {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    gap: 0.5rem;
    align-content: start;
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
    border-radius: 12px;
    padding: 0.75rem;
  }
  /* Every child is placed explicitly, row included. Auto-placement would not do
     it: a full-width row (the password on a narrow card) advances the cursor
     past row 1, so the remove button after it in DOM order lands on a row of its
     own instead of beside the username. Rows are not subgridded -- only columns
     are -- so these row numbers are local to each credential. */
  .sbd-cred > * { min-inline-size: 0; }
  .sbd-cred > .sbd-cred-user   { grid-column: 1 / 2;   grid-row: 1; }
  .sbd-cred > .sbd-cred-remove { grid-column: -2 / -1; grid-row: 1; }
  .sbd-cred > .sbd-cred-pass   { grid-column: 1 / -1;  grid-row: 2; }
  .sbd-cred > .sbd-keys        { grid-column: 1 / -1;  grid-row: 3; }
  .sbd-cred > .sbd-hint        { grid-column: 1 / -1;  grid-row: 4; }
  @container sbd-card (inline-size >= 34rem) {
    .sbd-creds { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto; }
    /* Password joins the username's row, so the two blocks below move up one. */
    .sbd-cred > .sbd-cred-pass { grid-column: 2 / 3;  grid-row: 1; }
    .sbd-cred > .sbd-keys      { grid-row: 2; }
    .sbd-cred > .sbd-hint      { grid-row: 3; }
  }

  .sbd-keys { container-type: inline-size; container-name: sbd-keys; }
  .sbd-key-rows { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0.5rem; margin-block-end: 0.5rem; }
  .sbd-key-row { display: grid; grid-template-columns: subgrid; grid-column: 1 / -1; gap: 0.5rem; align-items: center; }
  .sbd-key-row > .sbd-tag { grid-column: 1 / -1; }
  @container sbd-keys (inline-size >= 30rem) {
    .sbd-key-rows { grid-template-columns: clamp(9rem, 24%, 13rem) minmax(0, 1fr) auto; }
    .sbd-key-row > .sbd-tag { grid-column: auto; }
  }

  /* These are now grid CELLS wrapping a TextField, not inputs themselves: the
     rows use the same labelled M3 field the Username/Password above do. They had
     been bare inputs with only a placeholder, so the moment a value was typed
     nothing said which column was which -- and the label vanished exactly when
     the value needed explaining. min-inline-size:0 is what lets a long key
     shrink inside its track instead of forcing the row wider. */
  .sbd-input,
  .sbd-tag { min-inline-size: 0; }
  /* Address and public key are read character by character. */
  .sbd-input :global(input) { font-family: 'Menlo', 'Consolas', monospace; }

  /* Row remove buttons are thumb-sized -- these used to be 32px icon buttons. */
  .sbd-remove {
    inline-size: 44px; block-size: 44px;
    display: inline-flex; align-items: center; justify-content: center;
    justify-self: end; align-self: center;
    border: 0; border-radius: 50%;
    background: transparent; color: rgb(var(--md-sys-color-on-surface-variant));
    cursor: pointer;
  }
  .sbd-remove:hover { background: rgb(var(--md-sys-color-error) / 0.12); color: rgb(var(--md-sys-color-error)); }
  .sbd-remove .material-symbols-outlined { font-size: 20px; }

  .sbd-rowactions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  .sbd-chip {
    display: inline-flex; align-items: center; gap: 4px;
    min-block-size: 40px;
    padding: 0.4rem 0.875rem;
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
    border-radius: 20px;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    font-size: 13px; font-weight: 500;
    cursor: pointer;
  }
  .sbd-chip:hover { background: rgb(var(--md-sys-color-on-surface) / 0.06); color: rgb(var(--md-sys-color-on-surface)); }

  .sbd-actions {
    position: sticky;
    inset-block-end: 0;
    z-index: 2;
    display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.5rem;
    padding-block: 0.75rem;
    background: rgb(var(--md-sys-color-surface-container-high));
    border-block-start: 1px solid rgb(var(--md-sys-color-outline-variant));
  }
  /* The shell's content panel is the scroller and carries its own bottom
     padding, so a bar pinned at inset-block-end: 0 leaves a strip below it that
     scrolling content shows through. This skirt paints the bar's own background
     over that strip whatever its size, so this page never has to hard-code the
     shell's padding and drift from it later. */
  .sbd-actions::after {
    content: '';
    position: absolute;
    inset-inline: 0;
    inset-block-start: 100%;
    block-size: 3rem;
    background: inherit;
    pointer-events: none;
  }
  .sbd-btn {
    min-block-size: 44px;
    padding: 0 20px;
    border: 0; border-radius: 22px;
    font-size: 14px; font-weight: 500;
    color: rgb(var(--md-sys-color-primary));
    background: transparent;
    cursor: pointer;
  }
  .sbd-btn:hover { background: rgb(var(--md-sys-color-primary) / 0.08); }
  .sbd-btn-primary { padding: 0 24px; background: rgb(var(--md-sys-color-primary)); color: rgb(var(--md-sys-color-on-primary)); }
  .sbd-btn-primary:hover { opacity: 0.9; }
  .sbd-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
